import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

export const UnitsSoldRankingChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, entities } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!finance?.transactions) return [];

        const unitsByProduct: Record<string, { name: string; units: number }> = {};

        for (const tx of finance.transactions) {
            if (['product_sale', 'sale'].includes(tx.category as string) && tx.type === 'REVENUE') {
                const id = tx.entityId as string;
                if (!unitsByProduct[id]) {
                    const entity = entities?.find((e: any) => e.id === id);
                    unitsByProduct[id] = {
                        name: (entity?.name as string) || id,
                        units: 0,
                    };
                }
                unitsByProduct[id].units += 1;
            }
        }

        return Object.values(unitsByProduct)
            .sort((a, b) => b.units - a.units)
            .slice(0, 10);
    }, [finance?.transactions, entities]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
                    >
                        <CartesianGrid
                            horizontal={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            type="number"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            width={120}
                            tickFormatter={(value: string) =>
                                value.length > 18 ? `${value.slice(0, 18)}...` : value
                            }
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                value,
                                t('charts.unitsSold', 'Units Sold'),
                            ]}
                        />
                        <Bar
                            dataKey="units"
                            fill={CHART_COLORS.accent}
                            radius={[0, 3, 3, 0]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
